import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Card, Spinner, OverlayTrigger, Tooltip, Button, Alert, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  achievementService,
  MyAchievementEntry,
  PublicAchievementRecent,
} from '../services/api';
import { achievementCircleColor } from '../utils/achievementRarity';
import { formatAchievementRarity } from '../utils/formatAchievementRarity';
import { achievementIconSrcFromKey } from '../utils/achievementIconAsset';
import AchievementShareTemplate from './AchievementShareTemplate';
import {
  captureAchievementShareCard,
  getQuizterSiteUrl,
} from '../utils/shareAchievementImage';
import './ProfileAchievementsSection.css';
import './AchievementCelebrationModal.css';

const MAX_STRIP = 5;

type StripEntry = MyAchievementEntry | PublicAchievementRecent;

type Props =
  | { variant: 'mine'; className?: string; sharePreviewPlayerName?: string }
  | { variant: 'public'; userId: string; className?: string; sharePreviewPlayerName?: string };

function AchievementCircle({
  iconAssetKey,
  ariaLabel,
  rarity,
}: {
  rarity: string;
  iconAssetKey?: string | null;
  ariaLabel: string;
}) {
  const bg = achievementCircleColor(rarity);
  const iconSrc = achievementIconSrcFromKey(iconAssetKey);

  return (
    <div
      className="profile-achievement-circle d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ backgroundColor: bg }}
      aria-label={ariaLabel}
      role="img"
    >
      {iconSrc ? (
        <img src={iconSrc} alt="" className="profile-achievement-circle-img" />
      ) : (
        <i className="bi bi-trophy-fill profile-achievement-fallback-icon" aria-hidden="true" />
      )}
    </div>
  );
}

const ProfileAchievementsSection: React.FC<Props> = (props) => {
  const { t, ready, i18n } = useSafeTranslation();
  const navigate = useNavigate();
  const className = props.className ?? '';
  const variant = props.variant;
  const publicUserId = variant === 'public' ? props.userId : undefined;
  const sharePreviewPlayerName = props.sharePreviewPlayerName;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [mineItems, setMineItems] = useState<MyAchievementEntry[]>([]);
  const [publicStrip, setPublicStrip] = useState<PublicAchievementRecent[]>([]);
  const [totalUnlocked, setTotalUnlocked] = useState(0);

  const [shareCaptureEntry, setShareCaptureEntry] = useState<StripEntry | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const shareTemplateRef = useRef<HTMLDivElement>(null);

  const badgeAria = useCallback(
    (name: string, rarityRaw: string, points: number) => {
      const rl = formatAchievementRarity(rarityRaw, t);
      if (ready) {
        return i18n.t('achievements.ariaBadge', { name, rarity: rl, points });
      }
      return `${name}, ${rl}, ${points} points`;
    },
    [t, ready, i18n]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      if (variant === 'mine') {
        const list = await achievementService.getMine();
        setMineItems(list);
        setTotalUnlocked(list.length);
      } else if (publicUserId) {
        const [recent, totalRes] = await Promise.all([
          achievementService.getPublicRecent(publicUserId, MAX_STRIP),
          achievementService.getPublicUnlockedTotal(publicUserId),
        ]);
        setPublicStrip(recent);
        setTotalUnlocked(totalRes.totalUnlocked);
      }
    } catch {
      setLoadError(true);
      setMineItems([]);
      setPublicStrip([]);
      setTotalUnlocked(0);
    } finally {
      setLoading(false);
    }
  }, [variant, publicUserId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onLang = () => {
      void load();
    };
    i18n.on('languageChanged', onLang);
    return () => {
      i18n.off('languageChanged', onLang);
    };
  }, [i18n, load]);

  const shareTemplateProps = useMemo(() => {
    if (!shareCaptureEntry) return null;
    const a = shareCaptureEntry;
    const rawNick = (sharePreviewPlayerName ?? '').trim().replace(/^@+/, '');
    const nick = rawNick || t('achievements.shareDefaultPlayer', 'Quizter Club player');
    const rarityLabel = formatAchievementRarity(a.rarity, t);
    const playerLineText = ready
      ? i18n.t('achievements.shareCardPlayerLine', { name: nick })
      : `Player: ${nick}`;
    const rarityLineText = ready
      ? i18n.t('achievements.shareCardRarityLine', { rarity: rarityLabel })
      : `Rarity: ${rarityLabel}`;
    const headlineText = ready
      ? i18n.t('achievements.shareHeadline', { name: a.name })
      : `I unlocked the achievement: ${a.name}!`;
    const descriptionText = a.description?.trim() ?? '';
    let unlockedDateLineText = '';
    if (a.unlockedAt) {
      const d = new Date(a.unlockedAt);
      if (!Number.isNaN(d.getTime())) {
        const dateStr = new Intl.DateTimeFormat(i18n.language || 'en', {
          dateStyle: 'long',
        }).format(d);
        unlockedDateLineText = ready
          ? i18n.t('achievements.shareCardUnlockedOn', { date: dateStr })
          : `Unlocked on ${dateStr}`;
      }
    }
    return {
      playerLineText,
      iconAssetKey: a.iconAssetKey,
      rarity: a.rarity,
      rarityLineText,
      siteUrl: getQuizterSiteUrl(),
      headlineText,
      descriptionText,
      unlockedDateLineText,
    };
  }, [shareCaptureEntry, sharePreviewPlayerName, t, ready, i18n]);

  useEffect(() => {
    if (!shareCaptureEntry) return;
    let cancelled = false;
    (async () => {
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      await new Promise<void>((r) => setTimeout(r, 48));
      const el = shareTemplateRef.current;
      if (!el || cancelled) {
        setPreviewLoading(false);
        setShareCaptureEntry(null);
        return;
      }
      try {
        const blob = await captureAchievementShareCard(el);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPreviewBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch {
        toast.error(
          t('achievements.shareError', 'Could not generate or share the image. Try again.')
        );
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
          setShareCaptureEntry(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shareCaptureEntry, t]);

  const openSharePreview = useCallback((entry: StripEntry) => {
    if (previewLoading) return;
    setPreviewLoading(true);
    setShareCaptureEntry(entry);
  }, [previewLoading]);

  const closePreview = useCallback(() => {
    setPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const showMore = totalUnlocked > MAX_STRIP;
  const handleShowMore = () => {
    if (variant === 'mine') {
      navigate('/achievements');
    } else if (publicUserId) {
      navigate(`/profile/${publicUserId}/achievements`);
    }
  };

  const tooltipBody = (
    name: string,
    rarityRaw: string,
    points: number,
    description?: string | null
  ) => {
    const rl = formatAchievementRarity(rarityRaw, t);
    return (
      <div className="achievement-tooltip-content small">
        <div className="achievement-tooltip-title">{name}</div>
        {description?.trim() ? (
          <div className="achievement-tooltip-desc">{description.trim()}</div>
        ) : null}
        <div className="achievement-tooltip-meta">{rl}</div>
        <div className="achievement-tooltip-meta">
          {t('achievements.pointsLabel', 'Points')}: {points}
        </div>
      </div>
    );
  };

  const previewTitle = t('achievements.previewImageTitle', 'Image preview');
  const previewOpenLabel = t('achievements.profileOpenSharePreview', 'View share image');

  const renderStripItem = (a: StripEntry) => (
    <OverlayTrigger
      key={a.achievementId}
      placement="top"
      trigger={['hover', 'focus']}
      overlay={
        <Tooltip id={`ach-tooltip-${variant}-${a.achievementId}`}>
          {tooltipBody(a.name, a.rarity, a.points ?? 0, a.description)}
        </Tooltip>
      }
    >
      <div
        className="profile-achievement-trigger profile-achievement-trigger--clickable"
        tabIndex={0}
        role="button"
        aria-label={previewOpenLabel}
        aria-busy={previewLoading && shareCaptureEntry?.achievementId === a.achievementId}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSharePreview(a);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openSharePreview(a);
          }
        }}
      >
        <AchievementCircle
          rarity={a.rarity}
          iconAssetKey={a.iconAssetKey}
          ariaLabel={badgeAria(a.name, a.rarity, a.points ?? 0)}
        />
      </div>
    </OverlayTrigger>
  );

  const hasAchievementStrip =
    !loading &&
    !loadError &&
    ((variant === 'mine' && mineItems.length > 0) ||
      (variant === 'public' && publicStrip.length > 0));

  let main: React.ReactNode;

  if (loading) {
    main = (
      <Card className={`profile-achievements-card ${className}`} aria-busy="true">
        <Card.Header className="py-2 px-3">
          <small className="fw-bold">{t('achievements.sectionTitle', 'Achievements')}</small>
        </Card.Header>
        <Card.Body className="py-3 text-center">
          <Spinner animation="border" size="sm" role="status" />
          <span className="visually-hidden">{t('achievements.loadingSection', 'Loading achievements…')}</span>
        </Card.Body>
      </Card>
    );
  } else if (loadError) {
    main = (
      <Card className={`profile-achievements-card ${className}`}>
        <Card.Header className="py-2 px-3">
          <small className="fw-bold">{t('achievements.sectionTitle', 'Achievements')}</small>
        </Card.Header>
        <Card.Body className="py-3">
          <Alert variant="warning" className="small py-2 mb-2">
            {t(
              'achievements.profileSectionLoadError',
              'Could not load achievements. Check your connection and try again.'
            )}
          </Alert>
          <Button variant="outline-primary" size="sm" onClick={() => load()}>
            {t('achievements.retryLoad', 'Try again')}
          </Button>
        </Card.Body>
      </Card>
    );
  } else if (variant === 'mine') {
    const strip = mineItems.slice(0, MAX_STRIP);
    if (strip.length === 0) {
      main = (
        <Card className={`profile-achievements-card ${className}`}>
          <Card.Header className="py-2 px-3">
            <small className="fw-bold">{t('achievements.sectionTitle', 'Achievements')}</small>
          </Card.Header>
          <Card.Body className="py-3">
            <small className="text-muted">{t('achievements.emptyStrip', 'No achievements yet.')}</small>
          </Card.Body>
        </Card>
      );
    } else {
      main = (
        <Card className={`profile-achievements-card ${className}`}>
          <Card.Header className="py-2 px-3">
            <small className="fw-bold">{t('achievements.sectionTitle', 'Achievements')}</small>
          </Card.Header>
          <Card.Body className="py-3">
            <div
              className="profile-achievements-strip d-flex flex-wrap gap-2 align-items-center"
              aria-label={t('achievements.sectionTitle', 'Achievements')}
            >
              {strip.map((a) => renderStripItem(a))}
            </div>
            {showMore && (
              <Button variant="link" className="p-0 mt-2 small" onClick={handleShowMore}>
                {t('achievements.showMore', 'Show more')}
              </Button>
            )}
          </Card.Body>
        </Card>
      );
    }
  } else if (publicStrip.length === 0) {
    main = (
      <Card className={`profile-achievements-card ${className}`}>
        <Card.Header className="py-2 px-3">
          <small className="fw-bold">{t('achievements.sectionTitle', 'Achievements')}</small>
        </Card.Header>
        <Card.Body className="py-3">
          <small className="text-muted">{t('achievements.emptyStrip', 'No achievements yet.')}</small>
        </Card.Body>
      </Card>
    );
  } else {
    main = (
      <Card className={`profile-achievements-card ${className}`}>
        <Card.Header className="py-2 px-3">
          <small className="fw-bold">{t('achievements.sectionTitle', 'Achievements')}</small>
        </Card.Header>
        <Card.Body className="py-3">
          <div
            className="profile-achievements-strip d-flex flex-wrap gap-2 align-items-center"
            aria-label={t('achievements.sectionTitle', 'Achievements')}
          >
            {publicStrip.map((a) => renderStripItem(a))}
          </div>
          {showMore && (
            <Button variant="link" className="p-0 mt-2 small" onClick={handleShowMore}>
              {t('achievements.showMore', 'Show more')}
            </Button>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {main}
      {hasAchievementStrip && shareCaptureEntry && shareTemplateProps ? (
        <AchievementShareTemplate ref={shareTemplateRef} {...shareTemplateProps} />
      ) : null}

      {hasAchievementStrip ? (
        <Modal
          show={Boolean(previewBlobUrl)}
          onHide={closePreview}
          centered
          className="achievement-share-preview-modal"
          backdropClassName="achievement-share-preview-backdrop"
          dialogClassName="achievement-share-preview-dialog"
          contentClassName="achievement-share-preview-content"
          enforceFocus={false}
        >
          <Modal.Header closeButton closeLabel={t('common.close', 'Close')}>
            <Modal.Title id="profile-achievement-share-preview-title">{previewTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="achievement-share-preview-body p-0">
            {previewBlobUrl ? (
              <div className="achievement-share-preview-frame">
                <img src={previewBlobUrl} alt="" className="achievement-share-preview-img" />
              </div>
            ) : null}
          </Modal.Body>
        </Modal>
      ) : null}
    </>
  );
};

export default ProfileAchievementsSection;
